/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import { Row, Form, Steps } from 'antd';
import { Drawer } from 'components/gyramais';
import { useMutation, useQuery } from '@apollo/client';

import { formatAndNotificateError, formatDate } from 'utils';
import { useConfirmModalContext } from 'contexts/ConfirmModalContext/confirm-modal-context';

import * as graphql from './graphql';
import { SAVE_ADDRESS } from '../../graphql';
import { badesulMicrocreditoPartnerFormItems } from './inputs';
import './styles.less';

import StepHandler from './components/StepHandler';

const { Step } = Steps;

const getContext = (type) => {
  const contexts = {
    socio: 'sócio(a)',
    avalista: 'avalista',
    representantelegal: 'representante legal',
  };

  const context = contexts[type];

  return context ?? 'responsável';
};

const steps = {
  CREATE_NEW_USER: 0,
  COMPLETED: 1,
};

const { useForm } = Form;

const NewBadesulForm = ({
  visible,
  onClose,
  businessId,
  businessUser: selectedBusinessUserPointer,
  type,
}) => {
  const { requestConfirmation } = useConfirmModalContext();

  const [form] = useForm();
  const [loadingMutation, setLoadingMutation] = useState(false);
  const [fullStep, setFullStep] = useState(badesulMicrocreditoPartnerFormItems[0]);
  const [creatingCivilPartner, setCreatingCivilPartner] = useState(false);
  const [, setUpdateState] = useState(false);

  const resetFields = () => {
    form.resetFields();
    setFullStep(badesulMicrocreditoPartnerFormItems[0]);
    setCreatingCivilPartner(false);
  };

  const handleClose = () => {
    resetFields();
    onClose();
  };

  const {
    data: { business = {} } = {},
    loading: businessLoading,
    refetch,
  } = useQuery(graphql.BUSINESS, {
    variables: { id: businessId },
    skip: !businessId,
  });

  const [getUserByCpf, { loading: userByCpfLoading }] = useMutation(graphql.GET_USER_BY_CPF);

  const { data: { civilStatuses = [] } = {}, loading: civilStatusesLoading } = useQuery(
    graphql.CIVIL_STATUSES,
  );

  const [postalCodeToAddress, { loading: postalCodeLoading }] = useMutation(
    graphql.POSTAL_CODE_TO_ADDRESS,
    {
      onError: (error) => formatAndNotificateError(error),
      onCompleted: ({ postalCodeToAddress }) => {
        if (postalCodeToAddress) {
          const { prefix, street, district, city, state } = postalCodeToAddress;

          form.setFieldsValue({
            address_prefix: prefix,
            address_street: street,
            address_district: district,
            address_city: city,
            address_state: state,
          });
        }

        return true;
      },
    },
  );

  const [saveBusinessUserMutation, { loading: saveBusinessLoading }] = useMutation(
    graphql.SAVE_BUSINESS_USER,
    {
      onError: (error) => formatAndNotificateError(error),
    },
  );

  const [saveAddress, { loading: saveAddressLoading }] = useMutation(SAVE_ADDRESS, {
    onError: (error) => formatAndNotificateError(error),
  });

  const [completeBusinessUser, { loading: completeBusinessLoading }] = useMutation(
    graphql.COMPLETE_BUSINESS_USER,
    {
      onError: (error) => formatAndNotificateError(error),
      onCompleted: ({ completeBusinessUser }) => {
        if (completeBusinessUser) {
          refetch();
          handleClose();
        }
      },
    },
  );

  const [generateProofOfCivilStatus] = useMutation(graphql.GENERATE_PROOF_OF_CIVIL_STATUS, {
    onError: (error) => formatAndNotificateError(error),
  });

  const selectedBusinessUser = useMemo(() => {
    const users = business?.users;

    if (!users) return null;
    if (!selectedBusinessUserPointer?.id) return null;

    return users.find((user) => user.id === selectedBusinessUserPointer?.id);
  }, [selectedBusinessUserPointer, business]);

  useEffect(() => {
    if (!selectedBusinessUser) {
      form.setFieldsValue({
        cpf: null,
        typesValues: [...new Set(['avalista'].concat(selectedBusinessUser?.types || []))],
      });
      return;
    }

    const email = selectedBusinessUser?.user?.email;
    const isEmail = email?.includes('@');

    const partnerData = {};
    for (const key in selectedBusinessUser?.user?.civilPartner ?? {}) {
      if (Object.hasOwn(selectedBusinessUser?.user?.civilPartner, key)) {
        partnerData[`partner_${key}`] = selectedBusinessUser.user.civilPartner[key];
      }
    }
    for (const key in selectedBusinessUser?.user?.address ?? {}) {
      if (Object.hasOwn(selectedBusinessUser?.user?.address, key)) {
        partnerData[`address_${key}`] = selectedBusinessUser.user.address[key];
      }
    }

    const additionalInfo = selectedBusinessUser?.user?.additionalInfo || {};

    form.setFieldsValue({
      ...partnerData,
      ...selectedBusinessUser.user,
      ...additionalInfo,
      nationality: additionalInfo.nationality ?? 'Brasileira',
      occupation: Number(additionalInfo.occupation) || 1284, // Empresario
      birthDate:
        selectedBusinessUser.user?.birthDate &&
        formatDate(new Date(selectedBusinessUser.user?.birthDate)),
      email: isEmail ? email : '',
      typesValues: selectedBusinessUser.types
        ?.filter((type) => !['conjuge'].includes(type.value))
        .map((type) => type.value)
        .sort(),
      civilStatusId: selectedBusinessUser.user?.civilStatus?.id,
      idCardFileId: selectedBusinessUser.user?.idCard?.id,
      partnerIdCardFileId: selectedBusinessUser.user?.civilPartner?.idCard?.id,
      proofOfMariageOrDivorceFileId: selectedBusinessUser?.user?.proofOfMariageOrDivorce?.id,
    });
    setUpdateState((value) => !value);
  }, [selectedBusinessUser]);

  const mutations = {
    saveBusinessUser: {
      mutation: saveBusinessUserMutation,
      baseVariables: () => ({
        id: selectedBusinessUser?.id,
        businessId,
      }),
    },
    saveAddress: {
      mutation: saveAddress,
      baseVariables: () => ({
        id: selectedBusinessUser?.user?.address?.id,
        userId: selectedBusinessUser?.user?.id,
      }),
    },
    completeBusinessUser: {
      mutation: completeBusinessUser,
      baseVariables: () => ({
        id: selectedBusinessUser?.id,
      }),
    },
    postalCodeToAddress: {
      mutation: postalCodeToAddress,
    },
  };

  const inputsFromDatabase = {
    civilStatuses,
  };

  const handleSubmit = async () => {
    setLoadingMutation(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 100));
      await form.validateFields();
      const mutationName = await fullStep.mutationName;
      const { mutation, baseVariables } = mutations[mutationName];

      const variables = {
        ...baseVariables(),
        ...form.getFieldsValue(),
      };

      if (variables.withProof) {
        const { ip } = await (await fetch('https://api.ipify.org/?format=json')).json();

        await generateProofOfCivilStatus({
          variables: {
            ip,
            id: selectedBusinessUser.id,
          },
        });
      }

      const civilPartnerData = {};
      for (const key in variables) {
        if (Object.hasOwn(variables, key)) {
          if (key.includes('partner_')) {
            const [, realKey] = key.split('partner_');
            civilPartnerData[realKey] = variables[key];
          }

          if (key.includes('address_')) {
            const [, realKey] = key.split('address_');
            // GYR-1332: BadesulAddress manages the plain address fields (district/state/...).
            // Only fall back to the address_* prefill when the plain field has no value,
            // so a stale/empty prefill can't overwrite the freshly-entered address.
            const plainValue = variables[realKey];
            if (
              fullStep.key !== 'address' &&
              (plainValue === undefined || plainValue === null || plainValue === '')
            ) {
              variables[realKey] = variables[key];
            }
          }

          const inputConfig = fullStep.inputs.find((input) => input.value === key);
          if (inputConfig?.forceType === 'string') {
            variables[key] = String(variables[key]);
          }
        }
      }

      if (Object.keys(civilPartnerData)[0]) {
        variables.civilPartner = civilPartnerData;
      }

      if (fullStep.key === 'partner' && !creatingCivilPartner) {
        const {
          data: { userByCPF },
        } = await getUserByCpf({ variables: { cpf: civilPartnerData.cpf } });

        if (!userByCPF) {
          setCreatingCivilPartner(true);
          return;
        }

        variables.civilPartner.id = userByCPF.id;
      }

      const response = await mutation({ variables });

      const nextStep = fullStep.handleGetNextStep(response.data.saveBusinessUser);
      setFullStep(badesulMicrocreditoPartnerFormItems.find((item) => item.key === nextStep));
    } catch (e) {
      formatAndNotificateError(
        {
          message: `Ocorreram erros em ${e.errorFields.length} campos.`,
        },
        <ul>
          {' '}
          {e.errorFields?.map((error) => (
            <li>{error.errors[0]}</li>
          ))}
        </ul>,
      );
    } finally {
      setLoadingMutation(false);
    }
  };

  const isLoading =
    loadingMutation ||
    saveBusinessLoading ||
    businessLoading ||
    civilStatusesLoading ||
    saveAddressLoading ||
    userByCpfLoading ||
    completeBusinessLoading ||
    postalCodeLoading;

  const stepsToShow = badesulMicrocreditoPartnerFormItems.filter((step) =>
    step.showStep(selectedBusinessUser),
  );

  return (
    <Drawer
      visible={visible}
      className={`business-user-drawer ${visible ? 'active' : ''}`}
      maxWidth="71.5rem"
      closable={!isLoading}
      destroyOnClose
      onClose={() => {
        if (!selectedBusinessUser) {
          requestConfirmation({
            onOk: () => {
              handleClose();
            },
            onCancel: () => {},
          });
        } else {
          handleClose();
        }
      }}
      title={
        fullStep.key !== steps.COMPLETED &&
        `${selectedBusinessUser?.id ? 'Editar' : 'Adicionar'} ${getContext(type)}`
      }
      backButton={
        fullStep.handleGetLastStep(selectedBusinessUser) && {
          visible: true,
          disabled: isLoading,
          onClick: () => {
            const lastStepKey = fullStep.handleGetLastStep(selectedBusinessUser);

            setFullStep(
              badesulMicrocreditoPartnerFormItems.find((form) => form.key === lastStepKey),
            );
          },
        }
      }
      primaryButton={{
        id: 'continue-business-user-button',
        loading: isLoading,
        onClick: handleSubmit,
      }}
    >
      <div className="carousel-content">
        <div>
          <Steps className="steps" current={stepsToShow.indexOf(fullStep)} direction="horizontal">
            {stepsToShow.map((step) => (
              <Step key={step.key} title={step.name} />
            ))}
          </Steps>

          <div className="steps-content">
            <div className="drawer-body">
              <Form layout="vertical" form={form} className="new-partner-form">
                <Row gutter={12}>
                  <StepHandler
                    fullStep={fullStep}
                    selectedBusinessUser={selectedBusinessUser}
                    isLoading={isLoading}
                    business={business}
                    setUpdateState={setUpdateState}
                    form={form}
                    inputsFromDatabase={inputsFromDatabase}
                    mutations={mutations}
                    creatingCivilPartner={creatingCivilPartner}
                  />
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default NewBadesulForm;
